<template>
  <div class="page-content || payment">
    <div class="payment-header">
      <CustomIcon name="logo" class="payment-header-logo" />
      <h2 class="payment-title">
        {{ getTitle() }}
      </h2>
    </div>
    <h3 class="payment-subtitle">Your program</h3>
    <div class="icon-text-set">
      <IconText
        iconName="vr"
        text="VR exposure therapy plan curated by your therapist"
      />
      <IconText
        iconName="program"
        text="Your therapist can see what you’re looking at in VR and coach you in person or virtually"
      />
    </div>
    <h3 class="payment-subtitle">Your VR headset</h3>
    <div class="payment-image">
      <img
        :src="require('@/assets/images/ovrcome_vr_headset.png')"
        name="ovrcome-headset"
        alt="ovrcome-headset-image"
      />
    </div>
    <div class="radio-set">
      <RadioSelect
        :onClick="() => updateForm('headsetType', 'regular', true)"
        :isActive="formData.headsetType.value === 'regular'"
        label="Send me a VR headset for a regular phone"
      />
      <!-- <RadioSelect
        :onClick="() => updateForm('headsetType', 'none', false)"
        :isActive="formData.headsetType.value !== 'regular'"
        label="I’ll use my own VR headset for mobile phones"
      /> -->
    </div>
    <div v-if="formData.headsetType.value === 'regular'">
      <h3 class="payment-subtitle">Delivery address</h3>
      <FieldInput
        name="name"
        placeholder="e.g. Bob Smith"
        labelText="Full Name"
        :value="formData.name.value"
        :touched="formData.name.touched"
        :onBlur="() => updateForm('name', formData.name.value, true)"
        :onChange="(event) => updateForm('name', event.target.value, false)"
        :validator="(value) => validator['name'](value)"
      />
      <div class="field">
        <label for="street-address" class="field-label">Street Address</label>
        <input
          tabindex="0"
          ref="streetAddressLookUp"
          class="field-input"
          type="text"
          id="street-address"
          placeholder="e.g. Building number and street name"
          :value="formData.address.value"
          :touched="formData.address.touched"
          @focus="() => updateForm('address', formData.address.value, true)"
          @blur="() => updateForm('address', formData.address.value, true)"
          @change="(event) => updateForm('address', event.currentTarget.value)"
          @input.once="autoCompleteService()"
        />
      </div>
      <FieldInput
        name="apartment"
        labelText="Address Line 2 (optional)"
        placeholder="e.g. Unit/Suite/Apt number"
        :value="formData.apartment.value"
        :touched="formData.apartment.touched"
        :onBlur="() => updateForm('apartment', formData.apartment.value, true)"
        :onChange="
          (event) => updateForm('apartment', event.target.value, false)
        "
      />
      <FieldInput
        name="locality"
        placeholder="e.g. Dallas"
        labelText="City"
        :value="formData.locality.value"
        :touched="formData.locality.touched"
        :onBlur="() => updateForm('locality', formData.locality.value, true)"
        :onChange="(event) => updateForm('locality', event.target.value, false)"
        :validator="(value) => validator['locality'](value)"
      />
      <FieldSelector
        labelText="Country"
        :options="countries"
        placeholder="Select Country"
        :value="formData.countryAlpha2.value"
        :onChange="updateCountryField"
        :validator="(value) => validator['country'](value)"
      />
      <FieldSelector
        :disabled="!formData.countryId.value"
        placeholder="e.g. Texas"
        name="state-selector"
        type="selector"
        :autoComplete="false"
        labelText="State/Province"
        :options="states"
        :value="formData.stateId.value"
        :touched="formData.stateId.touched"
        :onBlur="() => updateForm('stateId', formData.stateId.value, true)"
        :onChange="(event) => updateForm('stateId', event.target.value, false)"
      />
      <FieldInput
        name="postal"
        labelText="Zipcode/Postcode"
        placeholder="e.g. 73301"
        :value="formData.postal.value"
        :touched="formData.postal.touched"
        :onBlur="() => updateForm('postal', formData.postal.value, true)"
        :onChange="(event) => updateForm('postal', event.target.value, false)"
        :validator="(value) => validator['postal'](value)"
      />
      <FieldInput
        name="phoneNumber"
        labelText="Phone number (Required for shipping)"
        placeholder="e.g. +1 555 555 5555"
        :value="formData.phoneNumber.value"
        :touched="formData.phoneNumber.touched"
        :onBlur="
          () => updateForm('phoneNumber', formData.phoneNumber.value, true)
        "
        :onChange="
          (event) => updateForm('phoneNumber', event.target.value, false)
        "
        :validator="(value) => validator['phoneNumber'](value)"
      />
    </div>
    <SummaryBox
      title="Pay Now"
      :listItems="getSummaryItems()"
      :summaryItem="getSubscriptionSummary()"
      footerText="Cancel anytime."
    />
    <p class="payment-text">
      We currently ship from the US and New Zealand. For overseas deliveries
      international shipping costs and timeframes will apply, as well as duty
      (where applicable).
    </p>
    <h3 class="payment-subtitle">Pay with</h3>
    <StripePaymentForm
      :onSubmit="submitFormData"
      label="Card number"
      :isPaymentRequired="true"
      :fieldIsComplete="(value) => (this.cardDetailsAreCompleted = value)"
    >
      <SnackbarPrompt v-if="warning" icon="warning" variation="warning">{{
        warning
      }}</SnackbarPrompt>
      <div v-if="!isErrorAfterPaying">
        <CustomButton :disabled="!formIsValid() || isLoading">
          <span v-if="!isLoading"> Confirm and pay</span>
          <LoadingIndicator v-else />
        </CustomButton>
      </div>
    </StripePaymentForm>
  </div>
</template>
<script>
import FieldInput from "@/components/FieldInput.vue";
import FieldSelector from "@/components/FieldSelector.vue";
import CustomButton from "@/components/CustomButton.vue";
import StripePaymentForm from "@/components/StripePaymentForm.vue";
import CustomIcon from "@/components/CustomIcon.vue";
import SummaryBox from "@/components/SummaryBox.vue";
import IconText from "@/components/IconText.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import RadioSelect from "@/components/RadioSelect.vue";
import GooglePlaces from "@/services/google-places";
import SnackbarPrompt from "@/components/SnackbarPrompt";
import { PAYMENT_FORMS, MODULES } from "@/config";
import { fillInAddress, therapistClientPaymentValidation } from "@/utils";
import { mapGetters } from "vuex";
import * as queryString from "query-string";

export default {
  data() {
    return {
      validator: therapistClientPaymentValidation,
      cardDetailsAreCompleted: false,
    };
  },
  props: {
    updateFormState: Function,
    submitFormData: Function,
    formData: Object,
    isLoading: Boolean,
    warning: String,
  },
  components: {
    FieldInput,
    FieldSelector,
    CustomIcon,
    CustomButton,
    StripePaymentForm,
    SummaryBox,
    LoadingIndicator,
    IconText,
    RadioSelect,
    SnackbarPrompt,
  },
  mounted() {
    this.initGooglePlaces();
  },
  computed: {
    ...mapGetters(MODULES.PAYMENT, {
      isErrorAfterPaying: "getIsErrorAfterPaying",
      orderSummary: "getOrderSummary",
    }),
    ...mapGetters(MODULES.PLAN, {
      therapistPlans: "getAllTherapistClientPlans",
    }),
    ...mapGetters(MODULES.COUNTRY, {
      countries: "getCountries",
      states: "getCountryStates",
    }),
  },
  methods: {
    getTitle() {
      const search = location.hash.replace("#/", "");

      if (search) {
        const obj = queryString.parse(search);

        if (obj["name"]) {
          return `${obj["name"]}, start your subscription`;
        }
      }

      return "Start your subscription";
    },
    updateForm(key, value, touched = false) {
      const { updateFormState } = this;

      updateFormState(PAYMENT_FORMS.therapistClient, key, value, touched);
    },
    updateCountryField(event) {
      const { updateForm } = this;
      const { dispatch } = this.$store;

      if (event) {
        event.preventDefault();
      }

      const countryObj = this.countries.find(
        (country) => event.target.value === country.value
      );

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, countryObj.id).then(
        () => {
          updateForm("countryId", countryObj.id, true);
          updateForm("countryAlpha2", event.target.value, true);
          updateForm("stateId", "", true); // Clear the stateId field
        }
      );
    },
    getSelectedPlan(planId) {
      return this.therapistPlans.find((p) => Number(p.id) === Number(planId));
    },
    calculateHeadsetPrice(plan) {
      if (!plan) {
        return 0;
      }

      const total = Number(plan.first_month_price.amount);
      const payment = Number(plan.price.amount);

      const calc = total - payment;

      return calc;
    },
    getSummaryItems() {
      const { orderSummary } = this;

      const subscriptionSummary = this.getSubscriptionSummary();

      if (!subscriptionSummary) {
        return;
      }

      const plan = this.getSelectedPlan(this.formData.plan_id.value);
      const headsetPrice = this.calculateHeadsetPrice(plan);

      let summaryHeadsetPrice = 0,
        deliveryCosts = null;

      if (orderSummary) {
        const { delivery_method } = orderSummary;

        summaryHeadsetPrice = Number(headsetPrice);

        if (delivery_method) {
          deliveryCosts = Number(delivery_method.amount.amount);
        }
      }

      let sumList = [
        {
          id: "subscription_cost",
          label: "Subscription start",
          rawValue: subscriptionSummary.rawValue,
          value: `$${subscriptionSummary.rawValue.toFixed(2)} USD`,
        },
        { ...this.getTotalCost() },
      ];

      if (this.formData.headsetType.value !== "none") {
        sumList = [
          {
            id: "vr_headset_price",
            value: `${
              headsetPrice === 0
                ? "Free"
                : `$${summaryHeadsetPrice.toFixed(2)} USD`
            }`,
            label: "VR Headset",
            rawValue: summaryHeadsetPrice,
          },
          {
            id: "shipping_price",
            value:
              deliveryCosts === 0
                ? "Free"
                : deliveryCosts !== null
                ? `$${deliveryCosts.toFixed(2)} USD`
                : "To be calculated",
            label: "Shipping",
            rawValue: summaryHeadsetPrice,
          },
          ...sumList,
        ];
      }

      return sumList;
    },
    getSubscriptionSummary() {
      const { plan_id } = this.formData;

      if (!plan_id) {
        return {
          id: "subscription_description",
          label: "Subscription",
          value: null,
          rawValue: 0,
        };
      }

      const selectedPlan = this.getSelectedPlan(plan_id.value);

      if (!selectedPlan) {
        return;
      }

      const billingUnits =
        selectedPlan.billing_units === "yearly"
          ? "year"
          : selectedPlan.billing_units;

      let billingText = null;

      billingText = `$${Number(
        selectedPlan.price.amount
      )}/${billingUnits} auto-renewing subscription will start immediately.`;

      return {
        id: "subscription_description",
        label: "Subscription",
        value: billingText,
        rawValue: Number(selectedPlan.price.amount),
      };
    },
    getTotalCost() {
      let summaryTotal = 0;
      let showTotal = false;

      if (this.orderSummary) {
        const { delivery_method } = this.orderSummary;
        const { plan_id, headsetType } = this.formData;

        const plan = this.getSelectedPlan(plan_id.value);
        const headsetPrice = this.calculateHeadsetPrice(plan);

        if (delivery_method) {
          showTotal = true;
          summaryTotal =
            headsetPrice +
            Number(plan.price.amount) +
            Number(delivery_method.amount.amount);
        } else if (headsetType.value === "none") {
          showTotal = true;
          summaryTotal = Number(plan.price.amount);
        }
      }

      return {
        id: "total_price",
        label: "Total",
        value: `${
          showTotal ? `$${summaryTotal.toFixed(2)}` : "To be calculated"
        }`,
        rawValue: summaryTotal,
      };
    },
    initGooglePlaces() {
      this.googleService = new GooglePlaces();
      this.googleService.init();

      this.autoComplete = new this.googleService.service.Autocomplete(
        this.$refs.streetAddressLookUp,
        {
          types: ["geocode"],
        }
      );
    },
    autoCompleteService() {
      this.autoComplete.addListener("place_changed", () => {
        const results = fillInAddress(this.autoComplete);

        this.autoCompleteForm(results);
        this.updateForm("phoneNumber", this.formData.phoneNumber.value, true);
      });
    },
    autoCompleteForm(results) {
      const blackListedKeys = ["country", "name", "stateId", "phoneNumber"];
      const keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      keys.forEach((key) => {
        if (properties(this.validator, key) && !blackListedKeys.includes(key)) {
          const replaceLocality = this.shouldReplaceLocality(key, results);

          this.updateForm(
            key,
            replaceLocality ? results.sublocality : results[key],
            false
          );
        }
      });

      this.updateCountryFromGoogle(results.countryAlpha, results?.state);
    },
    shouldReplaceLocality(key, results) {
      return key === "locality" && !results[key] && results.sublocality;
    },
    updateCountryFromGoogle(googleCountryAlpha, googleCountryState) {
      const findCountry = this.countries.find(
        (country) => googleCountryAlpha === country.value
      );

      this.updateForm("countryId", findCountry.id, false);
      this.updateForm("countryAlpha2", findCountry.value, false);

      this.getCountryStates(findCountry.id, googleCountryState);
    },
    getCountryStates(id, googleCountryState) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, id).then(() => {
        this.updateFormState(
          PAYMENT_FORMS.therapistClient,
          "stateId",
          "",
          false
        );

        this.updateCountryStateFromGoogle(googleCountryState);
      });
    },
    // use regex to remove special characters, accents and put in lowercase
    updateCountryStateFromGoogle(googleCountryState) {
      if (googleCountryState) {
        const normalizedGoogleCountryState = googleCountryState
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const state = this.states.find(
          (cstate) =>
            cstate.label
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase() === normalizedGoogleCountryState.toLowerCase()
        );
        if (state) {
          this.updateForm("stateId", state.value.toString(), true);
        }
      } else {
        this.updateForm("stateId", "", false);
      }
    },
    formIsValid() {
      let keys = Object.keys(this.formData);

      if (this.formData.headsetType.value !== "regular") {
        return this.cardDetailsAreCompleted;
      }

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          if (key === "stateId" && this.countryHasNoStates()) {
            return false;
          }

          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid && this.cardDetailsAreCompleted;
    },
    countryHasNoStates() {
      const { country } = this.formData;

      if (country.value && this.countryStates.length < 1) {
        return true;
      }

      return false;
    },
  },
};
</script>
